import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // Agregamos useNavigate
import api from '../../services/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const navigate = useNavigate(); // Inicializamos navigate para redirecciones

    const [stats, setStats] = useState({
        ventas_hoy: 0,
        ventas_mes: 0,
        tours_pendientes: 0,
        entregas_hoy: 0,
        envios_hoy: 0,
        productos_bajos: 0,
        ventas_totales: 0,
        tours_totales: 0,
        envios_totales: 0
    });
    
    const [ventasRecientes, setVentasRecientes] = useState([]);
    const [toursProximos, setToursProximos] = useState([]);
    const [enviosRecientes, setEnviosRecientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        cargarDashboard();
        const intervalo = setInterval(cargarDashboard, 30000);
        return () => clearInterval(intervalo);
    }, []);

    const cargarDashboard = async () => {
        try {
            setLoading(true);
            const ventasRes = await api.get('/ventas');
            const ventas = ventasRes.data;
            const toursRes = await api.get('/tours');
            const tours = toursRes.data;
            
            let envios = [];
            try {
                // Si ya no usas envíos, puedes quitar este bloque en el futuro
                const enviosRes = await api.get('/envios');
                envios = enviosRes.data;
            } catch (e) {
                console.warn('No se pudieron cargar los envíos');
            }
            
            const hoy = new Date().toISOString().split('T')[0];
            const inicioMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
            
            const ventasHoy = ventas.filter(v => v.created_at && v.created_at.startsWith(hoy));
            const ventasMes = ventas.filter(v => v.created_at && v.created_at >= inicioMes);
            const entregasHoy = ventas.filter(v => v.fecha_entrega_asignada === hoy);
            const enviosHoy = envios.filter(e => e.fecha_envio && e.fecha_envio.startsWith(hoy));
            const toursPendientes = tours.filter(t => t.estatus === 'pendiente');
            
            setStats({
                ventas_hoy: ventasHoy.length,
                ventas_mes: ventasMes.length,
                tours_pendientes: toursPendientes.length,
                entregas_hoy: entregasHoy.length,
                envios_hoy: enviosHoy.length,
                ventas_totales: ventas.length,
                tours_totales: tours.length,
                envios_totales: envios.length
            });
            
            setVentasRecientes(ventas.slice(0, 5));
            setToursProximos(toursPendientes.slice(0, 5));
            setEnviosRecientes(envios.slice(0, 5));
        } catch (error) {
            console.error('Error cargando dashboard:', error);
            setError('Error al cargar los datos del servidor.');
        } finally {
            setLoading(false);
        }
    };

    // FUNCIÓN PARA CONFIRMAR TOUR (Botón Palomita)
    const confirmarTour = async (id) => {
        if (window.confirm('¿Estás seguro de confirmar este tour?')) {
            try {
                // Asegúrate de que esta ruta coincida con tu backend PHP
                await api.put(`/tours/${id}`, { estatus: 'confirmado' });
                
                // Recargamos el dashboard para que desaparezca de "pendientes"
                cargarDashboard(); 
            } catch (error) {
                console.error("Error al confirmar el tour", error);
                alert("Hubo un error al actualizar el tour.");
            }
        }
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return 'No asignada';
        return new Date(fecha).toLocaleDateString('es-MX', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    };

    const formatearMoneda = (cantidad) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency', currency: 'MXN'
        }).format(cantidad || 0);
    };

    if (loading && ventasRecientes.length === 0) {
        return (
            <div className="admin-loading-screen">
                <div className="coffee-spinner">☕</div>
                <p>Cargando panel de control...</p>
            </div>
        );
    }

    return (
        <div className="admin-dashboard-container">
            <div className="dashboard-header">
                <div>
                    <h1>Panel de Administración</h1>
                    <p className="bienvenida">Bienvenido de nuevo, Román Valderrábano</p>
                </div>
                {error && <div className="error-banner">⚠️ {error}</div>}
            </div>
            
            {/* Tarjetas de estadísticas (Top Level) */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon-wrapper sales">💰</div>
                    <div className="stat-info">
                        <h3>Ventas Hoy</h3>
                        <p className="stat-number">{stats.ventas_hoy}</p>
                        <span className="stat-label success">Últimas 24h</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon-wrapper month">📦</div>
                    <div className="stat-info">
                        <h3>Ventas del Mes</h3>
                        <p className="stat-number">{stats.ventas_mes}</p>
                        <span className="stat-label">Total histórico: {stats.ventas_totales || 0}</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon-wrapper tours">🎫</div>
                    <div className="stat-info">
                        <h3>Tours Pendientes</h3>
                        <p className="stat-number">{stats.tours_pendientes}</p>
                        <span className="stat-label warning">De {stats.tours_totales || 0} totales</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon-wrapper deliveries">🚚</div>
                    <div className="stat-info">
                        <h3>Entregas Hoy</h3>
                        <p className="stat-number">{stats.entregas_hoy}</p>
                        <span className="stat-label info">Programadas para hoy</span>
                    </div>
                </div>
            </div>

            {/* Layout Principal: Dos columnas en Desktop */}
            <div className="dashboard-main-grid">
                
                {/* Columna Izquierda (Tablas) */}
                <div className="dashboard-left-col">
                    <div className="dashboard-card">
                        <div className="card-header">
                            <h2>Ventas Recientes</h2>
                            <Link to="/admin/ventas" className="view-all-btn">Ver todas</Link>
                        </div>
                        <div className="table-responsive">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Folio</th>
                                        <th>Cliente</th>
                                        <th>Total</th>
                                        <th>Estatus</th>
                                        <th>Entrega</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ventasRecientes.length > 0 ? ventasRecientes.map(venta => (
                                        <tr key={venta.id}>
                                            <td><Link to={`/admin/ventas/${venta.id}`} className="folio-link">{venta.folio || `#${venta.id}`}</Link></td>
                                            <td>{venta.cliente_nombre || 'Cliente General'}</td>
                                            <td className="monto fw-bold">{formatearMoneda(venta.total)}</td>
                                            <td><span className={`status-pill ${venta.estatus?.toLowerCase() || 'pendiente'}`}>{venta.estatus || 'Pendiente'}</span></td>
                                            <td><span className="guia-badge">🚚 Asignada</span></td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="5" className="text-center empty-state">No hay ventas recientes.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="dashboard-card">
                        <div className="card-header">
                            <h2>Próximos Tours</h2>
                            <Link to="/admin/tours" className="view-all-btn">Gestionar tours</Link>
                        </div>
                        <div className="tours-list">
                            {toursProximos.length > 0 ? toursProximos.map(tour => (
                                <div key={tour.id} className="tour-item">
                                    <div className="tour-avatar">👥</div>
                                    <div className="tour-info-main">
                                        <strong>{tour.nombre || 'Tour General'}</strong>
                                        <div className="tour-meta">
                                            <span>📅 {formatearFecha(tour.fecha_reserva)}</span>
                                            <span>• {tour.personas || 2} personas</span>
                                        </div>
                                    </div>
                                    <span className={`status-pill ${tour.estatus?.toLowerCase() || 'pendiente'}`}>{tour.estatus || 'Pendiente'}</span>
                                    <div className="tour-actions">
                                        {/* Botones funcionales */}
                                        <button 
                                            onClick={() => confirmarTour(tour.id)} 
                                            className="btn-icon check" 
                                            title="Confirmar Tour"
                                        >
                                            ✓
                                        </button>
                                        <button 
                                            onClick={() => navigate(`/admin/tours`)} 
                                            className="btn-icon view" 
                                            title="Ver detalles"
                                        >
                                            👁️
                                        </button>
                                    </div>
                                </div>
                            )) : (
                                <p className="empty-state">No hay tours pendientes.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Columna Derecha (Acciones y Widgets) */}
                <div className="dashboard-right-col">
                    <div className="dashboard-card quick-actions-card">
                        <h2>Acciones Rápidas</h2>
                        <div className="actions-grid">
                            <Link to="/admin/ventas" className="action-btn">
                                <span className="action-icon">📋</span> Nueva Venta
                            </Link>
                            <Link to="/admin/productos" className="action-btn">
                                <span className="action-icon">☕</span> Inventario
                            </Link>
                            <Link to="/admin/entregas" className="action-btn">
                                <span className="action-icon">🚚</span> Entregas
                            </Link>
                            <a href="http://localhost/phpmyadmin" target="_blank" rel="noopener noreferrer" className="action-btn db-btn">
                                <span className="action-icon">🗄️</span> Base de Datos
                            </a>
                        </div>
                    </div>

                    {/* Widget Modificado a Entregas */}
                    <div className="dashboard-card estafeta-widget" style={{ background: 'linear-gradient(135deg, #2E1B10 0%, #5D4037 100%)'}}>
                        <div className="estafeta-header">
                            <h3>🚚 Román Valderrábano El Catador</h3>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#EFEBE9', marginBottom: '1rem' }}>
                            Café de Especialidad en Xicotepec, Puebla. Gestión de logística interna.
                        </p>
                        <div className="estafeta-actions">
                            <button 
                                onClick={() => navigate('/admin/entregas')} 
                                className="btn-estafeta outline"
                            >
                                Programar Entrega
                            </button>
                            <button 
                                onClick={() => navigate('/admin/entregas')} 
                                className="btn-estafeta solid"
                                style={{ background: '#C09A4D', color: '#1A1A1A' }}
                            >
                                Ver Entregas de Hoy
                            </button>
                        </div>
                    </div>

                    <div className="dashboard-card activity-widget">
                        <h2>Resumen Rápido</h2>
                        <div className="activity-item">
                            <div className="activity-icon">💸</div>
                            <div className="activity-details">
                                <h4>Ingreso Estimado Mes</h4>
                                <p className="monto-grande">{formatearMoneda(ventasRecientes.reduce((acc, v) => acc + (v.total || 0), 0))}</p>
                            </div>
                        </div>
                        <div className="activity-item">
                            <div className="activity-icon">👤</div>
                            <div className="activity-details">
                                <h4>Último Cliente</h4>
                                <p>{ventasRecientes[0]?.cliente_nombre || 'N/A'}</p>
                                <small>{formatearFecha(ventasRecientes[0]?.created_at)}</small>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminDashboard;